import { createFileRoute } from "@tanstack/react-router"
import { IconFileText, IconClock, IconNews, IconGavel, IconEye, IconShield, IconArrowRight } from "@tabler/icons-react"
import { Link } from "@tanstack/react-router"
import { PageCTASection } from "@/components/ui/PageCTASection"
import {
  Card,
  Button,
  Text,
  Center,
  Box,
  useMantineColorScheme,
  ActionIcon,
  Title,
  Stack,
  Grid,
  Container,
  Group,
} from "@mantine/core"
import { SEO } from "@/components/seo/SEO"
import { StructuredData, createOrganizationSchema, createWebSiteSchema } from "@/components/seo/StructuredData"
import { ShareButtons } from "@/components/ui/ShareButtons"
import { BreakingDevelopments } from "@/components/home/BreakingDevelopments"

export const Route = createFileRoute("/")({
  component: HomePage,
})

function HomePage() {
  const { colorScheme } = useMantineColorScheme()

  return (
    <>
      <SEO
        title="Advocating for Complete Transparency and Justice"
        description="Demanding the immediate release of ALL unredacted documents related to the Jeffrey Epstein case. Access court documents, depositions, and evidence to ensure justice and accountability for all involved parties."
        canonical="https://rtefn.com"
        keywords="Epstein files, Jeffrey Epstein, court documents, transparency, justice, accountability, document release, Epstein case, public records, government accountability, full disclosure, unredacted documents"
        ogType="website"
        ogImage="https://rtefn.com/og-default.png"
        ogImageAlt="Release The Epstein Files Now - Transparency, Justice, Accountability"
      />
      <StructuredData data={[createOrganizationSchema(), createWebSiteSchema()]} />
      <Box className="animate-fade-in">
        {/* Hero Section */}
        <Box
          className={`min-h-screen flex items-center justify-center relative overflow-hidden py-16 ${
            colorScheme === "dark" ? "hero-dark" : "bg-gradient-to-br from-gray-50 via-white to-blue-50"
          }`}
        >
          {colorScheme === "dark" && <Box className="noise-overlay" />}

          <Container size="xl" className="relative z-10">
            <Stack ta="center" maw={1280} mx="auto">
              {/* Tagline */}
              <Box className="animate-fade-in-up" style={{ animationDelay: "0.2s", marginBottom: "var(--space-3xl)" }}>
                <Box
                  component="span"
                  className="inline-block backdrop-blur-md text-lg font-medium rounded-full"
                  style={{
                    letterSpacing: "0.025em",
                    padding: "12px 28px",
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
                    border:
                      colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
                    color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(55, 65, 81, 1)",
                  }}
                >
                  Transparency • Justice • Accountability
                </Box>
              </Box>

              {/* Main Headline */}
              <h1
                className="animate-fade-in-up"
                style={{
                  animationDelay: "0.4s",
                  fontFamily: "var(--font-family)",
                  fontWeight: 700,
                  letterSpacing: "-0.04em",
                  lineHeight: 0.9,
                  fontSize: "clamp(3.5rem, 9vw, 9rem)",
                  margin: "0 0 var(--space-2xl) 0",
                  color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a",
                }}
              >
                Release The
                <br />
                <span className="gradient-text">Epstein Files Now</span>
              </h1>

              {/* Subtitle */}
              <div className="animate-fade-in-up" style={{ animationDelay: "0.6s" }}>
                <p
                  className="max-w-4xl mx-auto"
                  style={{
                    fontFamily: "var(--font-family)",
                    fontWeight: 400,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.5,
                    fontSize: "clamp(1.25rem, 3vw, 1.75rem)",
                    color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "#6b7280",
                    margin: "0 0 var(--space-3xl) 0",
                  }}
                >
                  The public deserves complete transparency. Access court documents, depositions, and evidence to ensure
                  justice and accountability for all involved parties.
                </p>
              </div>

              {/* CTA Buttons */}
              <Box className="animate-fade-in-up" mt="xl" mb="3xl" style={{ animationDelay: "0.8s" }}>
                <PageCTASection
                  contextualAction={{ href: "/documents", label: "Explore Documents", icon: IconFileText }}
                />
              </Box>

              {/* Legal Notice */}
              <Box className="animate-fade-in" style={{ animationDelay: "1s" }}>
                <Text size="sm" lh={1.6} c="dimmed">
                  All documents and information are publicly available and legally obtained
                </Text>
              </Box>
            </Stack>
          </Container>

          {/* Scroll indicator */}
          <Box className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <Box
              className="w-6 h-10 border-2 rounded-full flex justify-center"
              style={{
                borderColor: colorScheme === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(156, 163, 175, 0.3)",
              }}
            >
              <Box
                className="w-1 h-3 rounded-full mt-2 animate-pulse"
                style={{
                  backgroundColor: colorScheme === "dark" ? "rgba(255, 255, 255, 0.4)" : "rgba(75, 85, 99, 0.5)",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Mission Statement */}
        <Box
          style={{
            background:
              colorScheme === "dark"
                ? "linear-gradient(180deg, #0a0a0a 0%, #0f0f23 100%)"
                : "linear-gradient(180deg, #ffffff 0%, #f3f4f6 100%)",
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
          }}
        >
          <Container size="xl">
            <Box maw={1152} mx="auto">
              <Card
                shadow="lg"
                radius="xl"
                p="3xl"
                style={{
                  background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  border:
                    colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
                }}
              >
                <Stack gap="xl">
                  <Group justify="center" gap="md" align="center">
                    <ActionIcon
                      variant="subtle"
                      size={64}
                      radius="md"
                      style={{ color: "var(--mantine-primary-color-filled)" }}
                    >
                      <IconGavel size={40} />
                    </ActionIcon>
                    <Title order={2} size="3rem" fw={700}>
                      Our <span className="gradient-text">Mission</span>
                    </Title>
                  </Group>

                  <Text size="xl" lh={1.6} maw={900} mx="auto" ta="center">
                    Justice requires <strong>complete, unredacted transparency</strong>. The Jeffrey Epstein case
                    involves powerful individuals and raises serious questions about accountability. We demand the
                    immediate release of ALL documents without government interference, selective editing, or political
                    manipulation. Recent "releases" contain 97% previously public material - we will not accept this
                    deception. The public deserves the <strong>full, unedited truth</strong>.
                  </Text>
                </Stack>
              </Card>
            </Box>
          </Container>
        </Box>

        {/* Breaking Developments Section */}
        <BreakingDevelopments />

        {/* Features Section */}
        <Box
          style={{
            background:
              colorScheme === "dark"
                ? "linear-gradient(180deg, #0f0f23 0%, #1a1a2e 100%)"
                : "linear-gradient(180deg, #f3f4f6 0%, #e5e7eb 100%)",
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
          }}
        >
          <Container size="xl">
            <Stack ta="center" mb="4xl">
              <Title order={2} size="3rem" fw={700} mb="xl" ta="center">
                Comprehensive <span className="gradient-text">Resources</span>
              </Title>
              <Text size="xl" lh={1.6} maw={900} mx="auto" ta="center" c="dimmed" mb="xl">
                Access organized, verified information about the case from multiple perspectives and sources
              </Text>
            </Stack>

            <Grid gutter="xl">
              {/* Documents */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  shadow="lg"
                  radius="xl"
                  p="2xl"
                  h="100%"
                  className="group transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <Stack gap="md" h="100%">
                    <Center>
                      <Box
                        p="md"
                        style={{
                          backgroundColor: "var(--mantine-primary-color-filled)",
                          borderRadius: "var(--mantine-radius-md)",
                          boxShadow: "var(--mantine-shadow-lg)",
                        }}
                      >
                        <IconFileText size={32} style={{ color: "var(--mantine-primary-color-contrast)" }} />
                      </Box>
                    </Center>

                    <Text size="2xl" fw={600} ta="center">
                      Court Documents
                    </Text>

                    <Text size="base" ta="center" c="dimmed" lh={1.6} style={{ flex: 1 }}>
                      Official court documents, depositions, exhibits, and legal filings. All materials are publicly
                      available with verification status clearly marked.
                    </Text>

                    <Button
                      component={Link}
                      to="/documents"
                      variant="light"
                      fullWidth
                      rightSection={<IconArrowRight size={16} />}
                      style={{
                        background: colorScheme === "dark" ? "rgba(59, 130, 246, 0.1)" : "rgba(59, 130, 246, 0.05)",
                        color: "#3b82f6",
                        border: "1px solid rgba(59, 130, 246, 0.2)",
                      }}
                    >
                      Browse Documents
                    </Button>
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Timeline */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  shadow="lg"
                  radius="xl"
                  p="2xl"
                  h="100%"
                  className="group transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <Stack gap="md" h="100%">
                    <Center>
                      <Box
                        p="md"
                        style={{
                          backgroundColor: "var(--mantine-primary-color-filled)",
                          borderRadius: "var(--mantine-radius-md)",
                          boxShadow: "var(--mantine-shadow-lg)",
                        }}
                      >
                        <IconClock size={32} style={{ color: "var(--mantine-primary-color-contrast)" }} />
                      </Box>
                    </Center>

                    <Text size="2xl" fw={600} ta="center">
                      Case Timeline
                    </Text>

                    <Text size="base" ta="center" c="dimmed" lh={1.6} style={{ flex: 1 }}>
                      Interactive timeline of key events, arrests, court proceedings, and document releases. Track the
                      case progression from initial allegations to current status.
                    </Text>

                    <Button
                      component={Link}
                      to="/timeline"
                      variant="light"
                      fullWidth
                      rightSection={<IconArrowRight size={16} />}
                      style={{
                        background: colorScheme === "dark" ? "rgba(139, 92, 246, 0.1)" : "rgba(139, 92, 246, 0.05)",
                        color: "#8b5cf6",
                        border: "1px solid rgba(139, 92, 246, 0.2)",
                      }}
                    >
                      View Timeline
                    </Button>
                  </Stack>
                </Card>
              </Grid.Col>

              {/* News */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  shadow="lg"
                  radius="xl"
                  p="2xl"
                  h="100%"
                  className="group transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <Stack gap="md" h="100%">
                    <Center>
                      <Box
                        p="md"
                        style={{
                          backgroundColor: "var(--mantine-primary-color-filled)",
                          borderRadius: "var(--mantine-radius-md)",
                          boxShadow: "var(--mantine-shadow-lg)",
                        }}
                      >
                        <IconNews size={32} style={{ color: "var(--mantine-primary-color-contrast)" }} />
                      </Box>
                    </Center>

                    <Text size="2xl" fw={600} ta="center">
                      Latest News
                    </Text>

                    <Text size="base" ta="center" c="dimmed" lh={1.6} style={{ flex: 1 }}>
                      Stay informed about new document releases, court decisions, and investigative reporting from
                      credible sources covering case developments.
                    </Text>

                    <Button
                      component={Link}
                      to="/news"
                      variant="light"
                      fullWidth
                      rightSection={<IconArrowRight size={16} />}
                      style={{
                        background: colorScheme === "dark" ? "rgba(16, 185, 129, 0.1)" : "rgba(16, 185, 129, 0.05)",
                        color: "#10b981",
                        border: "1px solid rgba(16, 185, 129, 0.2)",
                      }}
                    >
                      Read Updates
                    </Button>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          </Container>
        </Box>

        {/* Principles Section */}
        <Box
          style={{
            paddingTop: "var(--mantine-spacing-xl)",
            paddingBottom: "var(--mantine-spacing-xl)",
            backgroundColor: "var(--mantine-color-body)",
          }}
        >
          <Container size="xl">
            <Stack ta="center" mb="xl">
              <Text size="2rem" fw={600} mb="md" component="h2">
                Our Principles
              </Text>
              <Text size="lg" maw={800} mx="auto" c="dimmed">
                Guided by transparency, accuracy, and respect for the legal process
              </Text>
            </Stack>

            <Grid gutter="xl">
              {/* Transparency */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Stack ta="center">
                  <Center mb="md">
                    <ActionIcon
                      variant="subtle"
                      size={48}
                      radius="md"
                      style={{
                        color: "var(--mantine-primary-color-filled)",
                      }}
                    >
                      <IconEye size={24} />
                    </ActionIcon>
                  </Center>
                  <Text size="lg" fw={600} mb="sm">
                    Transparency
                  </Text>
                  <Text c="dimmed">
                    All sources are clearly documented with warnings about government manipulation and selective
                    releases
                  </Text>
                </Stack>
              </Grid.Col>

              {/* Accuracy */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Stack ta="center">
                  <Center mb="md">
                    <ActionIcon
                      variant="subtle"
                      size={48}
                      radius="md"
                      style={{
                        color: "var(--mantine-primary-color-filled)",
                      }}
                    >
                      <IconShield size={24} />
                    </ActionIcon>
                  </Center>
                  <Text size="lg" fw={600} mb="sm">
                    Accuracy
                  </Text>
                  <Text c="dimmed">
                    Information is fact-checked with explicit warnings about administration interference and selective
                    editing
                  </Text>
                </Stack>
              </Grid.Col>

              {/* Legal Compliance */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Stack ta="center">
                  <Center mb="md">
                    <ActionIcon
                      variant="subtle"
                      size={48}
                      radius="md"
                      style={{
                        color: "var(--mantine-primary-color-filled)",
                      }}
                    >
                      <IconGavel size={24} />
                    </ActionIcon>
                  </Center>
                  <Text size="lg" fw={600} mb="sm">
                    Legal Compliance
                  </Text>
                  <Text c="dimmed">
                    All content respects legal boundaries and focuses only on publicly available information
                  </Text>
                </Stack>
              </Grid.Col>
            </Grid>
          </Container>
        </Box>

        {/* Call to Action */}
        <Box
          ta="center"
          style={{
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
            backgroundColor: colorScheme === "dark" ? "#030712" : "#111827",
          }}
        >
          <Container size="xl">
            <Stack maw={896} mx="auto" ta="center">
              <Title order={2} size="3rem" fw={600} mb="md" c="white">
                Justice Through Transparency
              </Title>

              <Text size="xl" c="gray.3" mb="xl" maw={768} mx="auto" fw={300} lh={1.6}>
                The pursuit of justice requires an informed public. Explore the available documents and timeline to
                better understand this important case and its implications.
              </Text>

              <Stack hiddenFrom="sm" gap="lg">
                <Link
                  to="/action"
                  className="apple-button apple-button-primary text-lg px-8 py-4 min-w-[180px] font-medium bg-white text-gray-900 hover:bg-gray-100"
                >
                  Take Action
                </Link>
              </Stack>
              <Group visibleFrom="sm" gap="lg" justify="center">
                <Link
                  to="/action"
                  className="apple-button apple-button-primary text-lg px-8 py-4 min-w-[180px] font-medium bg-white text-gray-900 hover:bg-gray-100"
                >
                  Take Action
                </Link>
              </Group>

              {/* Share Section */}
              <Box mt="2xl">
                <Text size="sm" c="gray.4" mb="sm" ta="center" fw={500}>
                  Help spread awareness
                </Text>
                <Group justify="center">
                  <ShareButtons size="lg" variant="filled" />
                </Group>
              </Box>
            </Stack>
          </Container>
        </Box>
      </Box>
    </>
  )
}
